import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { cn } from '../../lib/utils.ts';

/**
 * Reiter sitzen immer in einer Karte, nie frei auf dem grauen Grund (DESIGN „Segment / Reiter“, [RUDOLF 19.10.2026]):
 *
 * - Wechseln die Reiter den Inhalt **einer** Karte, sitzen sie im Kopf dieser Karte anstelle des Titels
 *   (`CardHeadTabs` + `useCardHeadTabs`, z. B. Seitenleiste „Kennzahlen · Verlauf“ der Artikelansicht).
 * - Wechseln sie **mehrere** Karten oder Seiten, sitzen sie in einer eigenen Reiterkarte (`TabCard`) direkt über diesen
 *   Karten, optional mit dem Titel davor (Bereiche des Datensatzes, Auswertungen).
 * - Ausnahme Einstellungen (Ticket 1.258, DESIGN „Reiter der Einstellungen“, [RUDOLF 21.10.2026]): deren Reiter stehen im
 *   Seitenkopf neben „Einstellungen › <Hauptpunkt>“ (`SettingsTabs` in `pages/settings.tsx`), nicht in einer Reiterkarte.
 *
 * Die Listenkarte (`ListView`/`ViewTabs`) hat ihre Reiter schon in der Karte und nutzt diesen Baustein nicht.
 */
export function TabCard({
  title,
  titleTestId,
  children,
  className,
  testId,
  aside,
  ref,
  active,
  edgeFade,
}: {
  /** Überschrift vor den Reitern in derselben Karte (z. B. Hauptpunkt der Einstellungen). */
  title?: ReactNode;
  titleTestId?: string;
  children: ReactNode;
  className?: string;
  testId?: string;
  /** Rechts neben den Reitern in derselben Karte (z. B. Schalter-Abzeichen der Finanzen); am Handy darunter. */
  aside?: ReactNode;
  /** Die Reiterzeile (waagerecht wischbar), z. B. zum Scrollen des aktiven Reiters in den Blick. */
  ref?: Ref<HTMLDivElement>;
  /**
   * Gewählter Reiter (opt-in, Sichtung HS54 zu Story 23.6): Passt die Leiste nicht in die Karte (Handy), blendet sie am Rand
   * aus, wo noch Reiter folgen, und holt den aktiven Reiter ganz in den Blick – kein abgeschnittenes Wort ohne Hinweis.
   */
  active?: string;
  /**
   * Nur Rand-Verlauf (Story 12.6, Auflage HS54): blendet am Rand aus, wo noch Reiter folgen, ohne den aktiven Reiter in den
   * Blick zu holen (Sprungleisten, die selbst scrollen). `active` schließt den Verlauf ein.
   */
  edgeFade?: boolean;
}) {
  const own = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const follow = active !== undefined;
  const edgesOn = follow || edgeFade === true;
  const measure = useCallback(() => {
    const el = own.current;
    if (!el) return;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);
  useEffect(() => {
    if (!follow) return;
    const el = own.current;
    el?.querySelector<HTMLElement>('[aria-checked="true"],[aria-selected="true"],[aria-pressed="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    measure();
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [follow, active, measure]);
  useEffect(() => {
    if (follow || !edgeFade) return;
    const el = own.current;
    measure();
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // Story 5.122: auch die Reiter selbst – kommen Reiter nachträglich dazu (Daten geladen), ändert sich nur der Inhalt.
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [follow, edgeFade, measure]);
  const fade = `linear-gradient(to right, ${edges.start ? 'transparent' : 'black'}, black 56px, black calc(100% - 56px), ${edges.end ? 'transparent' : 'black'})`;
  const scroll = (
    <div
      ref={(el) => {
        own.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      onScroll={edgesOn ? measure : undefined}
      data-rand-links={edgesOn && edges.start ? '' : undefined}
      data-rand-rechts={edgesOn && edges.end ? '' : undefined}
      style={edgesOn && (edges.start || edges.end) ? { maskImage: fade, WebkitMaskImage: fade } : undefined}
      className="min-w-0 overflow-x-auto [scrollbar-width:none]"
    >
      {children}
    </div>
  );
  const row = aside ? (
    <div className="flex min-w-0 flex-col gap-space-3 tablet:flex-row tablet:flex-wrap tablet:items-center tablet:justify-between">
      {scroll}
      {aside}
    </div>
  ) : (
    scroll
  );
  return (
    <div data-testid={testId} data-reiterkarte className={cn('min-w-0', title ? 'card-box flex flex-col gap-space-3' : null, className)}>
      {title ? (
        <h2 className="type-card-title" data-testid={titleTestId}>
          {title}
        </h2>
      ) : null}
      {row}
    </div>
  );
}

/** Reiter für den Kopf einer Karte: `title` = Titel der Karte, die sie trägt (ohne = jede Karte darunter, die fragt – nur um genau eine Karte legen). */
interface HeadTabs {
  title?: string;
  tabs: ReactNode;
}

const CardHeadTabsContext = createContext<HeadTabs | null>(null);

/**
 * Stellt Reiter für den Kopf einer Karte darunter bereit. Die Karte mit dem passenden Titel (`title`) zeigt sie statt ihrer
 * Überschrift; die Überschrift bleibt für Hilfsmittel als Name der Karte erhalten.
 */
export function CardHeadTabs({ title, tabs, children }: { title?: string; tabs: ReactNode | null; children: ReactNode }) {
  return <CardHeadTabsContext.Provider value={tabs ? { ...(title === undefined ? {} : { title }), tabs } : null}>{children}</CardHeadTabsContext.Provider>;
}

/** Reiter für den Kopf dieser Karte (oder `null`): Karten-Bausteine rufen es mit ihrem Titel auf. */
export function useCardHeadTabs(title: string): ReactNode | null {
  const ctx = useContext(CardHeadTabsContext);
  if (!ctx) return null;
  return ctx.title === undefined || ctx.title === title ? ctx.tabs : null;
}
